import React, { useState, useEffect } from 'react';
import { XMarkIcon, CalendarIcon, CurrencyDollarIcon, ClockIcon } from '@heroicons/react/24/outline';
import { subscriptionAPI, formatCurrency } from '../../services/api';
import toast from 'react-hot-toast';

const SubscriptionModal = ({ offer, isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    // Champs communs
    startDate: '',
    
    // Champs spécifiques épargne
    monthlyAmount: '',
    
    // Champs spécifiques prêt
    loanAmount: '',
    repaymentMonths: 6 // Valeur par défaut
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Fonction pour obtenir le 1er jour du mois suivant
  const getNextMonthFirstDay = () => {
    const today = new Date();
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    return nextMonth.toISOString().split('T')[0];
  };

  // Fonction pour obtenir la date d'aujourd'hui au format YYYY-MM-DD
  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Initialiser la date de début au 1er jour du mois suivant
  useEffect(() => {
    if (isOpen && !formData.startDate) {
      setFormData(prev => ({
        ...prev,
        startDate: getNextMonthFirstDay()
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Validation des champs
  const validateForm = () => {
    const newErrors = {};

    // Date de début obligatoire
    if (!formData.startDate) {
      newErrors.startDate = 'La date de début est obligatoire';
    } else {
      const selectedDate = new Date(formData.startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        newErrors.startDate = 'La date de début ne peut pas être dans le passé';
      }
    }

    // Validation spécifique selon le type d'offre
    if (offer?.type === 'SAVINGS') {
      const minimumAmount = offer?.price || 10000;
      const monthlyAmountValue = parseFloat(formData.monthlyAmount);
      if (!formData.monthlyAmount || monthlyAmountValue <= 0) {
        newErrors.monthlyAmount = 'Le montant mensuel est obligatoire et doit être positif';
      } else if (monthlyAmountValue < minimumAmount) {
        newErrors.monthlyAmount = `Le montant mensuel minimum est de ${new Intl.NumberFormat('fr-FR').format(minimumAmount)} FCFA`;
      }
    }

    if (offer?.type === 'LOAN') {
      const loanAmountValue = parseFloat(formData.loanAmount);
      if (!formData.loanAmount || loanAmountValue <= 0) {
        newErrors.loanAmount = 'Le montant du prêt est obligatoire et doit être positif';
      } else if (loanAmountValue < 50000) {
        newErrors.loanAmount = 'Le montant minimum du prêt est de 50 000 FCFA';
      } else if (loanAmountValue > 500000) {
        newErrors.loanAmount = 'Le montant maximum du prêt est de 500 000 FCFA';
      }

      if (!formData.repaymentMonths || formData.repaymentMonths < 1 || formData.repaymentMonths > 10) {
        newErrors.repaymentMonths = 'La durée de remboursement doit être entre 1 et 10 mois';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Préparer les métadonnées selon le type d'offre
      const metadata = {
        offerType: offer.type,
        startDate: formData.startDate
      };

      if (offer.type === 'SAVINGS') {
        metadata.monthlyAmount = parseFloat(formData.monthlyAmount);
      }

      if (offer.type === 'LOAN') {
        metadata.loanAmount = parseFloat(formData.loanAmount);
        metadata.repaymentMonths = parseInt(formData.repaymentMonths);
        metadata.interestRate = getInterestRate();
        metadata.monthlyPayment = calculateMonthlyPayment();
      }

      await subscriptionAPI.createSubscription({
        offerId: offer.id,
        metadata
      });
      
      toast.success('Demande de souscription envoyée avec succès !');
      onSuccess?.();
      onClose();
      
      // Reset form
      setFormData({
        startDate: getNextMonthFirstDay(),
        monthlyAmount: '',
        loanAmount: '',
        repaymentMonths: 6
      });
      setErrors({});
      
    } catch (error) {
      const message = error.response?.data?.error || 'Erreur lors de la souscription';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fonction pour formater un nombre avec séparateur de milliers
  const formatNumberWithSeparator = (value) => {
    if (!value) return '';
    // Supprimer tous les espaces existants et convertir en nombre
    const numValue = value.toString().replace(/\s/g, '');
    // Ajouter les espaces comme séparateurs de milliers
    return numValue.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  // Fonction pour nettoyer le nombre (supprimer les espaces)
  const cleanNumber = (value) => {
    return value.toString().replace(/\s/g, '');
  };

  const handleInputChange = (field, value) => {
    // Pour les champs de montant, nettoyer la valeur pour les calculs
    let cleanValue = value;
    if (field === 'monthlyAmount' || field === 'loanAmount') {
      cleanValue = cleanNumber(value);
    }

    // Bloquer uniquement les valeurs manifestement trop élevées pour le prêt
    if (field === 'loanAmount') {
      const numValue = parseFloat(cleanValue);
      if (cleanValue !== '' && numValue > 500000) {
        // Empêcher la saisie de valeurs supérieures à 500 000
        return;
      }
    }
    
    if (field === 'startDate') {
      // Pour la date : empêcher les dates antérieures à aujourd'hui
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Réinitialiser l'heure pour comparer seulement les dates
      
      if (value !== '' && selectedDate < today) {
        // Empêcher la saisie de dates dans le passé
        return;
      }
    }
    
    // Stocker la valeur nettoyée pour les calculs
    setFormData(prev => ({ ...prev, [field]: cleanValue }));
    
    // Effacer l'erreur du champ modifié
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Gestionnaire pour les touches du clavier (empêcher les caractères non numériques)
  const handleKeyPress = (e) => {
    // Permettre seulement les chiffres, backspace, delete, tab, escape, enter, flèches et espace
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', ' '];
    
    if (allowedKeys.includes(e.key)) {
      return;
    }
    
    // Permettre les chiffres (0-9)
    if (e.key >= '0' && e.key <= '9') {
      return;
    }
    
    // Empêcher tous les autres caractères
    e.preventDefault();
  };

  // Récupérer le taux d'intérêt du produit
  const getInterestRate = () => {
    if (offer?.features) {
      const interestRate = offer.features.find(feature => 
        feature.toLowerCase().includes('taux') || 
        feature.toLowerCase().includes('intérêt') ||
        feature.toLowerCase().includes('%')
      );
      
      if (interestRate) {
        // Extraire le pourcentage de la chaîne (ex: "Taux d'intérêt : 5%" -> 5)
        const match = interestRate.match(/(\d+(?:\.\d+)?)\s*%/);
        if (match) {
          return parseFloat(match[1]);
        }
      }
    }
    return 0;
  };

  // Calculer le paiement mensuel pour les prêts (avec intérêts)
  const calculateMonthlyPayment = () => {
    if (offer?.type === 'LOAN' && formData.loanAmount && formData.repaymentMonths) {
      const ratePercent = getInterestRate();
      const rate = ratePercent / 100; // Convertir en décimal
      
      // Formule : (Montant du prêt * (1 + Taux d'intérêt)) / Durée de remboursement
      const totalWithInterest = formData.loanAmount * (1 + rate);
      return Math.ceil(totalWithInterest / formData.repaymentMonths);
    }
    return 0;
  };

  if (!isOpen || !offer) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* En-tête */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Souscrire à {offer.name}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {offer.type === 'HEALTH_COVERAGE' && 'Couverture santé'}
              {offer.type === 'SAVINGS' && 'Plan d\'épargne'}
              {offer.type === 'LOAN' && 'Demande de prêt'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Date de début */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <CalendarIcon className="h-4 w-4 inline mr-2" />
              Date de début souhaitée *
            </label>
            <input
              type="date"
              value={formData.startDate}
              onChange={(e) => handleInputChange('startDate', e.target.value)}
              min={getTodayDate()}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                errors.startDate ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.startDate && (
              <p className="text-red-500 text-sm mt-1">{errors.startDate}</p>
            )}
            {formData.startDate && formData.startDate < getTodayDate() && (
              <p className="text-orange-600 text-xs mt-1">
                ⚠️ La date ne peut pas être antérieure à aujourd'hui
              </p>
            )}
          </div>

          {/* Champs spécifiques pour la couverture santé */}
          {offer.type === 'HEALTH_COVERAGE' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <CurrencyDollarIcon className="h-4 w-4 inline mr-2" />
                Montant mensuel
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formatCurrency(offer.price)}
                  disabled
                  className="w-full px-3 py-2 pr-16 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                />
              </div>
              <p className="text-gray-500 text-xs mt-1">
                Montant fixe défini par le produit
              </p>
            </div>
          )}

          {/* Champs spécifiques pour l'épargne */}
          {offer.type === 'SAVINGS' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <CurrencyDollarIcon className="h-4 w-4 inline mr-2" />
                Montant mensuel d'épargne *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formatNumberWithSeparator(formData.monthlyAmount)}
                  onChange={(e) => handleInputChange('monthlyAmount', e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Ex: 25 000"
                  className={`w-full px-3 py-2 pr-16 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.monthlyAmount ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                <span className="absolute right-3 top-2 text-gray-500 text-sm">FCFA</span>
              </div>
              {errors.monthlyAmount && (
                <p className="text-red-500 text-sm mt-1">{errors.monthlyAmount}</p>
              )}
              <p className="text-gray-500 text-xs mt-1">
                Montant minimum : {new Intl.NumberFormat('fr-FR').format(offer.price)} FCFA
              </p>
              {formData.monthlyAmount && parseFloat(formData.monthlyAmount) < offer.price && (
                <p className="text-orange-600 text-xs mt-1">
                  ⚠️ Le montant doit être d'au moins {new Intl.NumberFormat('fr-FR').format(offer.price)} FCFA
                </p>
              )}
            </div>
          )}

          {/* Champs spécifiques pour les prêts */}
          {offer.type === 'LOAN' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <CurrencyDollarIcon className="h-4 w-4 inline mr-2" />
                  Montant du prêt demandé *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={formatNumberWithSeparator(formData.loanAmount)}
                    onChange={(e) => handleInputChange('loanAmount', e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder="Ex: 200 000"
                    className={`w-full px-3 py-2 pr-16 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.loanAmount ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  <span className="absolute right-3 top-2 text-gray-500 text-sm">FCFA</span>
                </div>
                {errors.loanAmount && (
                  <p className="text-red-500 text-sm mt-1">{errors.loanAmount}</p>
                )}
                <p className="text-gray-500 text-xs mt-1">
                  Montant maximum : 500 000 FCFA
                </p>
                {formData.loanAmount && parseFloat(formData.loanAmount) > 500000 && (
                  <p className="text-orange-600 text-xs mt-1">
                    ⚠️ Le montant ne peut pas dépasser 500 000 FCFA
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <ClockIcon className="h-4 w-4 inline mr-2" />
                  Durée de remboursement *
                </label>
                <select
                  value={formData.repaymentMonths}
                  onChange={(e) => handleInputChange('repaymentMonths', parseInt(e.target.value))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.repaymentMonths ? 'border-red-300' : 'border-gray-300'
                  }`}
                >
                  {[...Array(10)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1} mois
                    </option>
                  ))}
                </select>
                {errors.repaymentMonths && (
                  <p className="text-red-500 text-sm mt-1">{errors.repaymentMonths}</p>
                )}
                
                {/* Calcul du paiement mensuel */}
                {formData.loanAmount && formData.repaymentMonths && (
                  <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Paiement mensuel estimé :</strong> {formatCurrency(calculateMonthlyPayment())}
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      {getInterestRate() > 0 
                        ? `(Intérêts inclus - Taux : ${getInterestRate()}%)`
                        : '(Aucun taux d\'intérêt défini pour ce produit)'
                      }
                    </p>
                  </div>
                )}
              </div>
            </>
          )}


          {/* Boutons */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Envoi...' : 'Confirmer la demande'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubscriptionModal;
